import type { FC } from 'react';
import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

/**
 * A CSV artifact, readable instead of downloadable (PA-11, decision D6).
 *
 * The rules are the platform's, not this component's:
 *
 * - **AG Grid alpine, no pagination.** A CSV report is a scan-it list; page numbers on
 *   ten thousand rows are furniture. Virtual scrolling, sticky header, every row.
 * - **Formatting by inferred type** — `MM/dd/yyyy`, `$1,234.56`, right-aligned numbers,
 *   `—` for empty. The file is machine output; the grid is what a person reads.
 * - **Nothing is uploaded back.** The bytes were fetched from the download endpoint
 *   once and parsed in the browser.
 *
 * Inference is per column and conservative: one non-conforming value and the column
 * stays text, because a date column with half its values reformatted is worse than no
 * reformatting at all.
 */

type ColumnKind = 'date' | 'currency' | 'number' | 'text';

const DATE_ISO = /^\d{4}-\d{2}-\d{2}(?:[ T][\d:.]+)?$/;
const DATE_US = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
const CURRENCY = /^-?\$\s?[\d,]+(?:\.\d+)?$/;
const DECIMAL = /^-?\d*\.\d+$/;
const INTEGER = /^-?\d+$/;

/** RFC 4180 with the minimum of ceremony: quotes, doubled quotes, CRLF. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];

    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (char !== '\r') {
      cell += char;
    }
  }

  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  return rows.filter((line) => line.some((value) => value !== ''));
}

const inferKind = (values: string[]): ColumnKind => {
  const filled = values.filter((value) => value.trim() !== '');
  if (filled.length === 0) {
    return 'text';
  }
  if (
    filled.every(
      (value) => DATE_ISO.test(value.trim()) || DATE_US.test(value.trim())
    )
  ) {
    return 'date';
  }
  if (filled.some((value) => CURRENCY.test(value.trim()))) {
    return filled.every(
      (value) => CURRENCY.test(value.trim()) || INTEGER.test(value.trim())
    )
      ? 'currency'
      : 'text';
  }
  if (
    filled.every(
      (value) => DECIMAL.test(value.trim()) || INTEGER.test(value.trim())
    )
  ) {
    return 'number';
  }
  return 'text';
};

const toDisplayDate = (raw: string): string => {
  const value = raw.trim();
  if (DATE_US.test(value)) {
    const [month, day, year] = value.split('/');
    return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
  }
  // ISO is parsed by hand, never by `new Date`: `2026-11-01` is UTC midnight, and a
  // viewer west of Greenwich would see 10/31 printed for a date the database stored
  // as 11/01.
  const iso = value.slice(0, 10).split('-');
  if (iso.length === 3) {
    const [year, month, day] = iso;
    return `${month}/${day}/${year}`;
  }
  return value;
};

const toNumber = (raw: string): number => Number(raw.replace(/[$\s,]/g, ''));

/** `—` for empty, never a blank cell, and never `$NaN`. */
export function formatCell(raw: string, kind: ColumnKind): string {
  const value = (raw ?? '').trim();
  if (value === '') {
    return '—';
  }
  if (kind === 'date') {
    return toDisplayDate(value);
  }
  if (kind === 'currency') {
    const amount = toNumber(value);
    return Number.isNaN(amount)
      ? value
      : `$${amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
  }
  if (kind === 'number') {
    const amount = toNumber(value);
    return Number.isNaN(amount)
      ? value
      : amount.toLocaleString('en-US', { maximumFractionDigits: 6 });
  }
  return value;
}

interface CsvGridProps {
  /** The raw CSV text, already fetched. */
  text: string;
  height?: number | string;
}

const CsvGrid: FC<CsvGridProps> = ({ text, height = '60vh' }) => {
  const { rows, columns, columnDefs } = useMemo(() => {
    const parsed = parseCsv(text);
    const header = parsed[0] ?? [];
    const body = parsed.slice(1);

    const kinds = header.map((_name, index) =>
      inferKind(body.map((row) => row[index] ?? ''))
    );

    const defs: ColDef[] = header.map((name, index) => ({
      headerName: name || `Column ${index + 1}`,
      colId: `c${index}`,
      field: `c${index}`,
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 120,
      type: kinds[index] === 'text' ? 'leftAligned' : 'rightAligned',
      valueFormatter: (params: ValueFormatterParams) =>
        formatCell(String(params.value ?? ''), kinds[index]),
    }));

    const data = body.map((row) => {
      const record: Record<string, string> = {};
      header.forEach((_name, index) => {
        record[`c${index}`] = row[index] ?? '';
      });
      return record;
    });

    return { rows: data, columns: header, columnDefs: defs };
  }, [text]);

  if (columns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        This document has no rows.
      </Typography>
    );
  }

  return (
    <Box data-testid="csv-grid">
      <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
        {rows.length.toLocaleString('en-US')}{' '}
        {rows.length === 1 ? 'row' : 'rows'} · {columns.length}{' '}
        {columns.length === 1 ? 'column' : 'columns'}
      </Typography>
      <Box className="ag-theme-alpine" sx={{ height, width: '100%', mt: 0.5 }}>
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={{ suppressHeaderMenuButton: true }}
          headerHeight={36}
          rowHeight={32}
          // No pagination, by rule: a report is a scroll, not a page.
          pagination={false}
          suppressPaginationPanel
          suppressCellFocus
          animateRows={false}
        />
      </Box>
    </Box>
  );
};

export default CsvGrid;
